import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAppSelector, useAppDispatch } from '../store/hooks';
import { selectIsAuthenticated, selectAuthLoading, getCurrentUser } from '../store/slices/authSlice';
import Layout from '../components/layout/Layout';

// Import pages directly
// @ts-ignore - Ignoring module resolution issues
import Dashboard from '../pages/Dashboard';
// @ts-ignore - Ignoring module resolution issues
import Enterprises from '../pages/Enterprises';
// @ts-ignore - Ignoring module resolution issues
import Profile from '../pages/Profile';
// @ts-ignore - Ignoring module resolution issues
import NotFound from '../pages/NotFound';
import Login from '../pages/Login';
import Signup from '../pages/Signup';
import Verify from '../pages/Verify';
import Invitation from '../pages/Invitation';
import AcceptInvitation from '../pages/AcceptInvitation';
import CompleteProfile from '../pages/CompleteProfile';
// @ts-ignore - Ignoring module resolution issues
import Trending from '../pages/Trending';
// @ts-ignore - Ignoring module resolution issues
import Insights from '../pages/Insights';
// @ts-ignore - Ignoring module resolution issues
import Content from '../pages/Content';
// Import wallet sub-pages
import Earnings from '../pages/wallet/Earnings';
import Referrals from '../pages/wallet/Referrals';
import Payouts from '../pages/wallet/Payouts';

// Placeholder components for routes that don't have dedicated pages yet
const Wallet = () => <div>Wallet Dashboard (Coming Soon)</div>;
const Settings = () => <div>Settings Page (Coming Soon)</div>;

// No longer needed with direct imports
// const LoadingFallback = () => (
//   <div style={{ 
//     display: 'flex', 
//     justifyContent: 'center', 
//     alignItems: 'center', 
//     height: '100vh',
//     color: '#fff'
//   }}>
//     Loading...
//   </div>
// );

// Route guard for protected routes
interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const loading = useAppSelector(selectAuthLoading);
  
  // DEVELOPMENT ONLY: Set to false to enforce authentication
  const DEV_BYPASS_AUTH = false;
  
  useEffect(() => {
    // Try to get the current user if we have a token but no user data
    if (!loading && !isAuthenticated && !DEV_BYPASS_AUTH) {
      dispatch(getCurrentUser());
    }
  }, [dispatch, isAuthenticated, loading, DEV_BYPASS_AUTH]);

  // Show loading state while checking authentication
  if (loading && !DEV_BYPASS_AUTH) {
    return <div>Loading...</div>;
  }

  if (!isAuthenticated && !DEV_BYPASS_AUTH) {
    // Redirect to login if not authenticated
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Route configuration with metadata for better maintainability
interface RouteConfig {
  path: string;
  element: React.ReactNode;
  protected?: boolean;
  title?: string;
  layout?: boolean;
}

const routes: RouteConfig[] = [
  {
    path: '/',
    element: <Navigate to="/login" replace />,
    protected: false,
    title: 'Home',
    layout: false,
  },
  {
    path: '/dashboard',
    element: <Dashboard />,
    protected: true,
    title: 'Home',
    layout: true,
  },
  {
    path: '/enterprises',
    element: <Enterprises />,
    protected: true,
    title: 'Enterprise',
    layout: true,
  },
  {
    path: '/trending',
    element: <Trending />,
    protected: true,
    title: 'Trending',
    layout: true,
  },
  {
    path: '/insights',
    element: <Insights />,
    protected: true,
    title: 'Insights',
    layout: true,
  },
  {
    path: '/content',
    element: <Content />,
    protected: true,
    title: 'Content',
    layout: true,
  },
  {
    path: '/wallet',
    element: <Wallet />,
    protected: true,
    title: 'Wallet',
    layout: true,
  },
  {
    path: '/wallet/earnings',
    element: <Earnings />,
    protected: true,
    title: 'Earnings',
    layout: true,
  },
  {
    path: '/wallet/referrals',
    element: <Referrals />,
    protected: true,
    title: 'Referrals',
    layout: true,
  },
  {
    path: '/wallet/payouts',
    element: <Payouts />,
    protected: true,
    title: 'Payouts',
    layout: true,
  },
  {
    path: '/profile',
    element: <Profile />,
    protected: true,
    title: 'Profile',
    layout: true,
  },
  {
    path: '/settings',
    element: <Settings />,
    protected: true,
    title: 'Settings',
    layout: true,
  },
  {
    path: '/login',
    element: <Login />,
    title: 'Sign In',
    layout: false,
  },
  {
    path: '/signup',
    element: <Signup />,
    title: 'Sign Up',
    layout: false,
  },
  {
    path: '/verify',
    element: <Verify />,
    title: 'Verify',
    layout: false,
  },
  {
    path: '/invitation',
    element: <Invitation />,
    title: 'Invitation',
    layout: false,
  },
  {
    path: '/accept-invitation',
    element: <AcceptInvitation />,
    title: 'Accept Invitation',
    layout: false,
  },
  {
    path: '/complete-profile',
    element: <CompleteProfile />,
    title: 'Complete Profile',
    layout: false,
  },
  {
    path: '*',
    element: <NotFound />,
    title: 'Page Not Found',
    layout: true,
  },
];

const AppRoutes: React.FC = () => {
  const location = useLocation();
  
  // Update document title based on route
  useEffect(() => {
    const currentRoute = routes.find(route => 
      route.path === location.pathname || 
      (route.path === '*' && !routes.some(r => r.path === location.pathname))
    );
    
    if (currentRoute?.title) {
      document.title = `BlinkLink - ${currentRoute.title}`;
    }
  }, [location]);
  
  return (
    <Routes>
      {routes.map((route) => {
        // Prepare the element with or without layout
        let element = route.element;
        
        // Apply layout if needed
        if (route.layout) {
          element = <Layout>{element}</Layout>;
        }
        
        // Apply protection if needed
        if (route.protected) {
          element = <ProtectedRoute>{element}</ProtectedRoute>;
        }
        
        return (
          <Route
            key={route.path}
            path={route.path}
            element={element}
          />
        );
      })}
    </Routes>
  );
};

export default AppRoutes;
